import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Package, Trash2, Pencil, Save, X, Search, Filter, Settings, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const UNITS = ["pz", "m", "m2", "kg", "l", "conf", "h", "kW"];
const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminCatalog() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ q: "", brand: "all", category: "all", minPrice: "", maxPrice: "", needsReview: false });
  const [editing, setEditing] = useState(null); // catalog_id or "new"
  const [form, setForm] = useState(blankForm());
  const [markupOpen, setMarkupOpen] = useState(false);

  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => { if (!loading && !isStaff) navigate("/login"); }, [user, loading, navigate, isStaff]);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.brand && filters.brand !== "all") params.set("brand", filters.brand);
      if (filters.category && filters.category !== "all") params.set("category", filters.category);
      if (filters.minPrice !== "") params.set("min_price", filters.minPrice);
      if (filters.maxPrice !== "") params.set("max_price", filters.maxPrice);
      params.set("limit", "1000");
      const [cat, brs] = await Promise.all([
        api.get(`/material-catalog?${params.toString()}`),
        api.get("/material-catalog/brands").catch(() => ({ data: [] })),
      ]);
      let data = cat.data;
      if (filters.needsReview) data = data.filter(d => d.needs_review);
      setItems(data);
      setBrands(brs.data || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [user]);

  const applyFilters = () => load();
  const resetFilters = () => { setFilters({ q: "", brand: "all", category: "all", minPrice: "", maxPrice: "", needsReview: false }); setTimeout(load, 50); };

  const markReviewed = async (it) => {
    try { await api.post(`/material-catalog/${it.catalog_id}/mark-reviewed`); toast.success("Articolo revisionato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const isPriceStale = (it) => {
    const d = it.price_checked_at || it.updated_at || it.created_at;
    if (!d) return false;
    const days = (Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24);
    return days > 180;
  };

  const startNew = () => { setForm(blankForm()); setEditing("new"); };
  const startEdit = (it) => {
    setForm({
      description: it.description || "", unit: it.unit || "pz",
      brand: it.brand || "", model: it.model || "", article_code: it.article_code || "",
      purchase_price: it.purchase_price == null ? "" : String(it.purchase_price),
      sell_price: String(it.sell_price ?? it.default_price ?? ""),
      category: it.category || "",
    });
    setEditing(it.catalog_id);
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!form.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const sp = parseFloat(form.sell_price || "0");
    if (Number.isNaN(sp) || sp < 0) { toast.error("Prezzo di vendita non valido"); return; }
    const pp = form.purchase_price === "" ? null : parseFloat(form.purchase_price);
    if (pp !== null && (Number.isNaN(pp) || pp < 0)) { toast.error("Prezzo di acquisto non valido"); return; }
    setBusy(true);
    try {
      const payload = {
        description: form.description.trim(),
        unit: form.unit,
        sell_price: sp,
        purchase_price: pp,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        article_code: form.article_code.trim() || null,
        category: form.category.trim() || null,
      };
      if (editing === "new") {
        await api.post("/material-catalog", payload);
        toast.success("Articolo aggiunto al catalogo");
      } else {
        await api.patch(`/material-catalog/${editing}`, payload);
        toast.success("Articolo aggiornato");
      }
      setEditing(null); await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.description}"?`)) return;
    try { await api.delete(`/material-catalog/${it.catalog_id}`); toast.success("Eliminato"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const margins = useMemo(() => items.map(it => {
    const sp = Number(it.sell_price ?? it.default_price ?? 0);
    const pp = it.purchase_price == null ? null : Number(it.purchase_price);
    if (pp == null || pp === 0) return null;
    return ((sp - pp) / pp) * 100;
  }), [items]);

  if (!isStaff) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Amministrazione</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <Package className="w-7 h-7"/> Catalogo materiali
            </h1>
            <p className="text-sm text-slate-600 mt-1">Il tuo prezziario cresce in automatico mano a mano che fai preventivi con l'IA. L'IA ti propone articoli già presenti e ti chiede di schedarne di nuovi quando serve.</p>
          </div>
          <Button onClick={startNew} className="bg-blue-800 hover:bg-blue-900" data-testid="cat-new-btn">
            <Plus className="w-4 h-4 mr-2"/> Nuovo articolo
          </Button>
          <Button variant="outline" onClick={() => setMarkupOpen(true)} data-testid="cat-markup-btn">
            <Settings className="w-4 h-4 mr-2"/> Markup
          </Button>
        </div>

        <Card className="p-4 mb-4">
          <div className="flex items-center gap-2 mb-3 text-slate-700 text-xs font-semibold uppercase tracking-widest">
            <Filter className="w-3.5 h-3.5"/> Filtri
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400"/>
              <Input placeholder="Cerca marchio, modello, codice, descrizione…"
                     value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                     onKeyDown={e => e.key === "Enter" && applyFilters()}
                     className="pl-9" data-testid="cat-filter-q"/>
            </div>
            <Select value={filters.brand} onValueChange={v => setFilters(f => ({ ...f, brand: v }))}>
              <SelectTrigger data-testid="cat-filter-brand"><SelectValue placeholder="Marchio"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i marchi</SelectItem>
                {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Categoria" value={filters.category === "all" ? "" : filters.category}
                   onChange={e => setFilters(f => ({ ...f, category: e.target.value || "all" }))}
                   onKeyDown={e => e.key === "Enter" && applyFilters()}
                   data-testid="cat-filter-cat"/>
            <Input type="number" placeholder="€ min" value={filters.minPrice}
                   onChange={e => setFilters(f => ({ ...f, minPrice: e.target.value }))}
                   onKeyDown={e => e.key === "Enter" && applyFilters()}
                   data-testid="cat-filter-min"/>
            <Input type="number" placeholder="€ max" value={filters.maxPrice}
                   onChange={e => setFilters(f => ({ ...f, maxPrice: e.target.value }))}
                   onKeyDown={e => e.key === "Enter" && applyFilters()}
                   data-testid="cat-filter-max"/>
          </div>
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={filters.needsReview}
                   onChange={e => { setFilters(f => ({ ...f, needsReview: e.target.checked })); setTimeout(load, 50); }}
                   data-testid="cat-filter-review"/>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600"/> Solo articoli da revisionare
          </label>
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="outline" onClick={resetFilters} size="sm" data-testid="cat-filter-reset">Reset</Button>
            <Button onClick={applyFilters} size="sm" className="bg-blue-800" data-testid="cat-filter-apply">Applica</Button>
          </div>
        </Card>

        {editing === "new" && (
          <Card className="p-4 mb-4 bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-slate-900 mb-3">Nuovo articolo</h3>
            <ItemForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy}/>
          </Card>
        )}

        {busy && items.length === 0 ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
            <p className="text-slate-600">Nessun articolo per questi filtri.</p>
          </Card>
        ) : (
          <Card className="p-0 overflow-x-auto" data-testid="cat-list">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-3 w-28">Marchio</th>
                  <th className="text-left px-3 py-3 w-36">Modello</th>
                  <th className="text-left px-3 py-3 w-28">Codice</th>
                  <th className="text-left px-3 py-3">Descrizione</th>
                  <th className="text-left px-3 py-3 w-14">UM</th>
                  <th className="text-right px-3 py-3 w-28">Acquisto</th>
                  <th className="text-right px-3 py-3 w-28">Vendita</th>
                  <th className="text-right px-3 py-3 w-20">Margine</th>
                  <th className="text-right px-3 py-3 w-24">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => editing === it.catalog_id ? (
                  <tr key={it.catalog_id} className="border-t bg-blue-50">
                    <td colSpan={9} className="p-3">
                      <ItemForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy}/>
                    </td>
                  </tr>
                ) : (
                  <tr key={it.catalog_id} className="border-t hover:bg-slate-50" data-testid={`cat-row-${it.catalog_id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{it.brand || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-700">{it.model || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono text-xs">{it.article_code || "—"}</td>
                    <td className="px-3 py-2 text-slate-900">
                      {it.description}
                      {it.category && <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{it.category}</span>}
                      {it.needs_review && <span title="Da revisionare" className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded"><AlertTriangle className="w-2.5 h-2.5"/>Da revisionare</span>}
                      {isPriceStale(it) && !it.needs_review && <span title="Prezzo non aggiornato da oltre 6 mesi" className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold bg-yellow-50 text-yellow-800 px-1.5 py-0.5 rounded border border-yellow-200">⏰ Prezzo vecchio</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{it.unit}</td>
                    <td className="px-3 py-2 text-right text-slate-600">
                      {it.purchase_price == null ? <span className="text-slate-300">—</span> : fmtEur(it.purchase_price)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtEur(it.sell_price ?? it.default_price)}</td>
                    <td className="px-3 py-2 text-right">
                      {margins[i] == null ? <span className="text-slate-300">—</span> :
                        <span className={margins[i] >= 20 ? "text-emerald-700 font-medium" : margins[i] >= 0 ? "text-slate-600" : "text-red-600 font-medium"}>
                          {margins[i].toFixed(0)}%
                        </span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {(it.needs_review || isPriceStale(it)) && (
                          <button onClick={() => markReviewed(it)} className="p-1.5 text-emerald-600 hover:text-emerald-800" title="Segna come revisionato" data-testid={`cat-review-${it.catalog_id}`}>
                            <CheckCircle2 className="w-4 h-4"/>
                          </button>
                        )}
                        <button onClick={() => startEdit(it)} className="p-1.5 text-slate-500 hover:text-blue-800" title="Modifica" data-testid={`cat-edit-${it.catalog_id}`}>
                          <Pencil className="w-4 h-4"/>
                        </button>
                        <button onClick={() => remove(it)} className="p-1.5 text-slate-500 hover:text-red-600" title="Elimina" data-testid={`cat-del-${it.catalog_id}`}>
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
      {markupOpen && <MarkupDialog onClose={() => setMarkupOpen(false)}/>}
    </div>
  );
}

function MarkupDialog({ onClose }) {
  const [cfg, setCfg] = useState({ default_pct: 30, by_category: {} });
  const [newCat, setNewCat] = useState("");
  const [newPct, setNewPct] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/settings/catalog-markup"); setCfg(r.data); }
      catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings/catalog-markup", cfg);
      toast.success("Markup salvato");
      onClose();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const addCat = () => {
    const c = (newCat || "").trim();
    const p = parseFloat(newPct);
    if (!c) { toast.error("Nome categoria obbligatorio"); return; }
    if (Number.isNaN(p) || p < 0) { toast.error("Percentuale non valida"); return; }
    setCfg(x => ({ ...x, by_category: { ...(x.by_category || {}), [c]: p } }));
    setNewCat(""); setNewPct("");
  };

  const removeCat = (c) => {
    setCfg(x => { const n = { ...(x.by_category || {}) }; delete n[c]; return { ...x, by_category: n }; });
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="markup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-blue-800"/> Markup automatico</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            Quando crei un articolo con solo il <b>prezzo acquisto</b>, il prezzo vendita viene
            calcolato automaticamente usando questa percentuale. Se la categoria ha un markup
            specifico, prevale quello.
          </p>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Markup di default (%)</label>
            <Input type="number" step="1" min="0" value={cfg.default_pct}
                   onChange={e => setCfg(x => ({ ...x, default_pct: parseFloat(e.target.value || "0") }))}
                   data-testid="markup-default"/>
            <p className="text-[10px] text-slate-400 mt-1">es: 30 ⇒ vendita = acquisto × 1,30</p>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Markup per categoria</label>
            {Object.entries(cfg.by_category || {}).length === 0 && <p className="text-xs text-slate-400 italic">Nessuna categoria configurata</p>}
            <div className="space-y-1.5">
              {Object.entries(cfg.by_category || {}).map(([c, p]) => (
                <div key={c} className="flex items-center gap-2">
                  <span className="flex-1 text-sm">{c}</span>
                  <Input type="number" value={p} className="w-20"
                         onChange={e => setCfg(x => ({ ...x, by_category: { ...x.by_category, [c]: parseFloat(e.target.value || "0") } }))}/>
                  <span className="text-xs text-slate-500">%</span>
                  <button onClick={() => removeCat(c)} className="p-1 text-slate-400 hover:text-red-600"><X className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-1.5">
              <Input placeholder="Nome categoria" value={newCat} onChange={e => setNewCat(e.target.value)} data-testid="markup-newcat"/>
              <Input type="number" placeholder="%" value={newPct} className="w-20"
                     onChange={e => setNewPct(e.target.value)} data-testid="markup-newpct"/>
              <Button onClick={addCat} variant="outline" size="sm">Aggiungi</Button>
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-800" data-testid="markup-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1"/>Salva</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function blankForm() {
  return { description: "", unit: "pz", brand: "", model: "", article_code: "",
           purchase_price: "", sell_price: "", category: "" };
}

function ItemForm({ form, setForm, onSave, onCancel, busy }) {
  const S = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
      <div>
        <L>Marchio</L>
        <Input value={form.brand} onChange={S("brand")} placeholder="es: BTICINO" data-testid="cat-form-brand"/>
      </div>
      <div>
        <L>Modello</L>
        <Input value={form.model} onChange={S("model")} placeholder="es: LivingNow" data-testid="cat-form-model"/>
      </div>
      <div>
        <L>Codice articolo</L>
        <Input value={form.article_code} onChange={S("article_code")} placeholder="es: N4511N" data-testid="cat-form-code"/>
      </div>
      <div className="md:col-span-3">
        <L>Descrizione *</L>
        <Input value={form.description} onChange={S("description")} placeholder="Es: Interruttore 1P 16A" data-testid="cat-form-desc"/>
      </div>
      <div>
        <L>UM</L>
        <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
          <SelectTrigger data-testid="cat-form-unit"><SelectValue/></SelectTrigger>
          <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <L>Prezzo acquisto (€)</L>
        <Input type="number" step="0.01" min="0" value={form.purchase_price} onChange={S("purchase_price")}
               placeholder="costo nostro" data-testid="cat-form-pprice"/>
      </div>
      <div>
        <L>Prezzo vendita (€) *</L>
        <Input type="number" step="0.01" min="0" value={form.sell_price} onChange={S("sell_price")}
               placeholder="in preventivo" data-testid="cat-form-sprice"/>
      </div>
      <div>
        <L>Categoria</L>
        <Input value={form.category} onChange={S("category")} placeholder="opzionale" data-testid="cat-form-cat"/>
      </div>
      <div className="md:col-span-2 flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="cat-form-cancel"><X className="w-4 h-4 mr-1"/>Annulla</Button>
        <Button onClick={onSave} disabled={busy} className="bg-blue-800" data-testid="cat-form-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1"/>Salva</>}
        </Button>
      </div>
    </div>
  );
}

function L({ children }) {
  return <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{children}</label>;
}
